import { formatRelativeTime } from "../../intelligence/intelligenceLabels";
import { collectionSummary, statusGuide } from "./dataSourceManageCopy";

function lower(value) {
  return String(value || "").toLowerCase();
}

function trunc(message, max = 160) {
  const text = String(message || "").trim();
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function latestRunOf(source) {
  return source?.latestRun || null;
}

function runWhen(source) {
  const latest = latestRunOf(source);
  const at = latest?.startedAt || latest?.finishedAt || source?.lastRunAt || null;
  if (!at) return null;
  return formatRelativeTime(at) || null;
}

function recordCounts(source) {
  const latest = latestRunOf(source);
  return {
    found: latest?.recordsReceived ?? latest?.recordsFound ?? source?.recordsFound ?? 0,
    created: latest?.recordsInserted ?? latest?.recordsCreated ?? source?.recordsCreated ?? 0,
    updated: latest?.recordsUpdated ?? source?.recordsUpdated ?? 0,
  };
}

function pushUnique(list, item) {
  if (!item) return;
  if (!list.includes(item)) list.push(item);
}

function overdueNextRun(source) {
  if (!source?.scheduleEnabled || !source?.nextRunAt) return false;
  const next = new Date(source.nextRunAt);
  return !Number.isNaN(next.getTime()) && next.getTime() < Date.now();
}

function listingCount(source) {
  if (source?.activeListingCount != null) return Number(source.activeListingCount);
  if (source?.listingCount != null) return Number(source.listingCount);
  return null;
}

/**
 * Operator-facing diagnosis for the data-source detail "Current state" panel.
 * Explains why the badge is set, what to inspect on this screen, and concrete next steps.
 */
export function buildSourceStatusDiagnosis(source, options = {}) {
  const {
    health: healthOverride = null,
    lastResultSource = null,
    errors = [],
    failedRunCount = 0,
    runBlockedReason = null,
    scheduleBlockedReason = null,
    canStop = false,
    isRunning = false,
    canManage = false,
  } = options;

  const view = lastResultSource || source;
  const health = lower(healthOverride || source?.health || "unknown");
  const evidence = [];
  const attention = [];
  const actions = [];
  const errorSnippet = trunc(errors[0] || source?.lastErrorMessage);
  const when = runWhen(view);
  const counts = recordCounts(view);
  const collection = collectionSummary(source);
  const listings = listingCount(source);

  const addAction = (action) => {
    if (!action?.id || actions.some((item) => item.id === action.id)) return;
    actions.push(action);
  };

  // --- evidence shared across states ---
  if (source && !source.enabled) {
    pushUnique(evidence, "Collection is turned off for this source.");
  } else if (collection) {
    pushUnique(evidence, `Collection mode: ${collection}.`);
  }

  if (when) {
    pushUnique(evidence, `Latest activity ${when}.`);
  }

  if (view?.lastRunAt || view?.latestRun) {
    pushUnique(
      evidence,
      `Last result found ${counts.found}, added ${counts.created}, updated ${counts.updated}.`
    );
  }

  if (errorSnippet) {
    pushUnique(evidence, `Recorded error: ${errorSnippet}`);
  }

  if (failedRunCount > 0) {
    pushUnique(
      evidence,
      `${failedRunCount} recent run${failedRunCount === 1 ? "" : "s"} finished with an error.`
    );
  }

  if (overdueNextRun(source)) {
    pushUnique(evidence, "Next scheduled run is overdue — the cadence may be stuck or delayed.");
  }

  if (listings != null) {
    pushUnique(evidence, `Active listings on file: ${listings}.`);
  }

  if (runBlockedReason) {
    pushUnique(evidence, runBlockedReason);
  }

  // --- state-specific summary + guidance ---
  let summary = statusGuide(health);
  let tone = "neutral";

  switch (health) {
    case "failed":
    case "error":
    case "failing":
      tone = "alert";
      summary = errorSnippet
        ? `The last collection failed: ${errorSnippet}`
        : "The last collection failed. Check the Errors card and the latest row in Recent runs.";
      pushUnique(attention, "Errors — start here for the failure message.");
      pushUnique(attention, "Recent runs — see whether this is a one-off or a repeating pattern.");
      pushUnique(attention, "Last result — confirm whether any records were still found.");
      if (!runBlockedReason && !isRunning) {
        addAction({ id: "run_now", label: "Retry collection", primary: true });
      }
      addAction({ id: "review_errors", label: "Review errors" });
      addAction({ id: "review_runs", label: "Check recent runs" });
      if (canManage) addAction({ id: "open_manage", label: "Manage settings" });
      break;

    case "warning":
    case "stale":
    case "degraded":
    case "completed_with_errors":
      tone = "warn";
      if (counts.found === 0 && (view?.lastRunAt || view?.latestRun)) {
        summary =
          "Showing warning because the latest collection finished without finding records. That often means a site/parser change, empty inventory, or a blocked fetch.";
        pushUnique(attention, "Last result — zero found is the main signal.");
        pushUnique(attention, "Recent runs — check if earlier runs still found inventory.");
        pushUnique(attention, "Source URL — open it and confirm listings are still on the site.");
      } else if (overdueNextRun(source)) {
        summary =
          "Showing warning because a scheduled run is overdue. Collection may still be enabled, but the cadence is behind.";
        pushUnique(attention, "Next run — overdue time in the status strip.");
        pushUnique(attention, "Recent runs — confirm the last attempt actually finished.");
      } else if (errorSnippet || failedRunCount > 0) {
        summary =
          "Showing warning because the latest collection finished with issues to review, even if it was not a hard failure.";
        pushUnique(attention, "Errors — review warnings or partial failures.");
        pushUnique(attention, "Recent runs — compare counts across attempts.");
      } else {
        summary =
          "Showing warning because the latest collection needs review (partial success, stale cadence, or soft issues).";
        pushUnique(attention, "Last result and Recent runs — verify counts and status.");
      }
      if (!runBlockedReason && !isRunning) {
        addAction({ id: "run_now", label: "Run now to re-check", primary: true });
      }
      addAction({ id: "review_runs", label: "Check recent runs" });
      if (errorSnippet || failedRunCount > 0) {
        addAction({ id: "review_errors", label: "Review errors" });
      }
      if (canManage) addAction({ id: "open_manage", label: "Manage schedule" });
      break;

    case "healthy":
    case "success":
    case "ok":
      tone = "ok";
      summary = when
        ? `The latest collection succeeded${counts.found != null ? ` and found ${counts.found} record${counts.found === 1 ? "" : "s"}` : ""}.`
        : "The latest collection succeeded.";
      if (source?.scheduleEnabled) {
        pushUnique(
          attention,
          source.nextRunAt
            ? "Next run — confirm the schedule is still the cadence you want."
            : "Collection is scheduled, but no next-run time is set — check Manage."
        );
      } else {
        pushUnique(attention, "Collection is manual-only — inventory will go stale unless you Run now or enable a schedule.");
      }
      pushUnique(attention, "Recent runs — keep an eye on found/added/updated trends.");
      if (!source?.scheduleEnabled && canManage) {
        addAction({ id: "open_manage", label: "Set a schedule", primary: true });
      }
      if (!runBlockedReason && !isRunning) {
        addAction({ id: "run_now", label: "Run now", primary: !source?.scheduleEnabled });
      }
      break;

    case "running":
    case "queued":
    case "started":
      tone = "info";
      summary =
        health === "queued"
          ? "A collection is queued and waiting to start."
          : "A collection is in progress. Wait for it to finish, or stop it if it is stuck.";
      pushUnique(attention, "Current state strip — elapsed time while the run is active.");
      pushUnique(attention, "Recent runs — the top row should move from running to a final status.");
      if (canStop) {
        addAction({ id: "stop", label: "Stop run", primary: true, danger: true });
      }
      addAction({ id: "refresh", label: "Refresh status" });
      break;

    case "never_run":
      tone = "warn";
      summary = "This source has never completed a collection, so there is no live inventory proof yet.";
      pushUnique(attention, "Run now — first successful collection is the next milestone.");
      pushUnique(attention, "Manage — confirm the source is enabled and scheduled if needed.");
      if (!runBlockedReason) {
        addAction({ id: "run_now", label: "Run first collection", primary: true });
      }
      if (canManage) addAction({ id: "open_manage", label: "Manage settings" });
      break;

    case "disabled":
      tone = "neutral";
      summary = "Collection is turned off, so this source will not run on a schedule or accept Run now until re-enabled.";
      pushUnique(attention, "Manage — enable collection if this source should be active.");
      if (canManage) addAction({ id: "open_manage", label: "Enable in Manage", primary: true });
      break;

    case "manual_ingest_validated":
    case "manual_ingest_validated_with_warnings":
      tone = health.includes("warning") ? "warn" : "ok";
      summary =
        health === "manual_ingest_validated_with_warnings"
          ? "Manual ingest was validated with warnings. This is a readiness milestone, not a live schedule/health guarantee."
          : "Manual ingest was validated once. This means Run now worked before — it does not mean inventory is currently fresh.";
      if (listings === 0) {
        pushUnique(
          evidence,
          "Active listings are 0 — re-run and confirm sync before treating this source as live."
        );
        pushUnique(attention, "Listings count — zero means nothing is currently published from this source.");
      }
      pushUnique(attention, "Last result — confirm the most recent Run now still finds records.");
      pushUnique(attention, "Collection / Next run — decide whether limited schedule should be on.");
      if (!runBlockedReason && !isRunning) {
        addAction({
          id: "run_now",
          label: listings === 0 ? "Run now (listings are 0)" : "Run now to refresh",
          primary: true,
        });
      }
      if (canManage) addAction({ id: "open_manage", label: "Review schedule" });
      addAction({ id: "review_runs", label: "Check recent runs" });
      break;

    case "manual_only":
      tone = "neutral";
      summary = "This source is limited to manual runs — schedule and public cadence stay off until promotion gates pass.";
      pushUnique(attention, "Run now — use it to keep inventory from going stale.");
      pushUnique(attention, "Manage — schedule may stay blocked until readiness/compliance gates clear.");
      if (!runBlockedReason && !isRunning) {
        addAction({ id: "run_now", label: "Run now", primary: true });
      }
      if (canManage) addAction({ id: "open_manage", label: "View settings" });
      break;

    default:
      tone = "neutral";
      summary = statusGuide(health);
      pushUnique(attention, "Last result and Recent runs — use them to judge whether action is needed.");
      if (!runBlockedReason && !isRunning && source?.supportsRunNow !== false) {
        addAction({ id: "run_now", label: "Run now" });
      }
      if (canManage) addAction({ id: "open_manage", label: "Manage settings" });
      break;
  }

  if (scheduleBlockedReason && !actions.some((a) => a.id === "open_manage") && canManage) {
    pushUnique(attention, scheduleBlockedReason);
  }

  if (!attention.length) {
    pushUnique(attention, "Status strip and Recent runs — confirm the latest outcome.");
  }

  return {
    health,
    tone,
    summary,
    evidence,
    attention,
    actions,
  };
}
